import React, { useEffect, useMemo } from 'react';

/* Translations */
import { useTranslation } from 'react-i18next';

/* Styled Components */
import { PermissionGroup } from '../styles/MemberConfig';

/* Components */
import RendererIf from 'components/RendererIf';
import PermissionsNoToggle from './PermissionsNoToggle';
import PermissionsWithToggle from './PermissionsWithToggle';

/* Contexts, Hooks, Services */
import usePermission from 'hooks/usePermission';
import { uniq } from 'ramda';
import useCurrentUser from 'hooks/useCurrentUser';
import { ActionPermission } from 'lib/specialPermissions';

/* Helpers */
import { idsArray } from 'components/settings/helpers';

interface SpecialPermissionsProps {
  memberId: any;
  memberType: string;
  memberPermissions: any[];
  enableButton: (data: any) => void;
  editedPermissions: any;
  handlePermissionChange: (data: any) => void;
}

const SpecialPermissions = ({
  memberId,
  memberType,
  memberPermissions = [],
  enableButton,
  editedPermissions,
  handlePermissionChange,
}: SpecialPermissionsProps) => {
  const { t, i18n } = useTranslation();

  /* Permissions Logic */
  const { fetchPermissions } = usePermission();
  const { can, user } = useCurrentUser();

  /* user needs to have special permission to edit and can not be viewing his own profile */
  const hasPermission = can(
    ActionPermission.GIVE_SPECIAL_PERMISSIONS_TO_DIRECTORS,
  );
  const isUserProfile = memberId === user.id;
  const canEdit = !isUserProfile && hasPermission;

  /**
   * DID MOUNT
   */
  useEffect(() => {
    fetchPermissions({ scope: 'director', locale: i18n.language });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const memberPermissionsIds: number[] = useMemo(
    () => idsArray(memberPermissions || []),
    [memberPermissions],
  );

  useMemo(() => {
    handlePermissionChange(memberPermissionsIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberPermissionsIds]);

  const checkPermissionStatus = (id: any) => memberPermissionsIds.includes(id);

  const handleEditPermissions = (id: any) => {
    let permissionsIds = editedPermissions;

    // If already exists then remove it, otherwise add it (toggle)
    if (editedPermissions.includes(id)) {
      // Remove only the id
      permissionsIds = permissionsIds.filter(
        (permissionId: number) => permissionId !== id,
      );
    } else {
      permissionsIds = uniq([...editedPermissions, id]);
    }

    handlePermissionChange(permissionsIds);
    enableButton(false);
  };

  return (
    <>
      <PermissionGroup>
        <h3>
          {t(
            'settings:STAFF_SECTION.MANAGE.ADD_MEMBER_VIEW.STEPS.PERMISSIONS.SPECIAL.TITLE',
          )}
        </h3>
        <RendererIf
          condition={canEdit}
          alternative={
            <PermissionsNoToggle
              memberType={memberType}
              memberPermissions={memberPermissions}
              checkPermission={checkPermissionStatus}
            />
          }
        >
          <PermissionsWithToggle
            handleEdit={handleEditPermissions}
            checkPermission={checkPermissionStatus}
          />
        </RendererIf>
      </PermissionGroup>
    </>
  );
};

export default SpecialPermissions;
